import React, { useState } from 'react';

import * as valid from 'utils/validators';
import PaperContainer from 'ui/PaperContainer';
import Fields from 'ui/form/FieldsForm';
import ButtonsForm from 'ui/form/ButtonsForm';
import StatusActionForm from 'ui/form/StatusActionForm';

import { extendByDefaultSchedule } from '../../../schedule';
import { HelpLink, AuthenticationInfo } from '../Forms';
import { PasswordField } from '../Components';

const validateHost = name => values => {
    const host = values[name];

    const isEmpty = valid.notEmpty(name)(values);
    if (isEmpty) {
        return isEmpty;
    }

    if (/http(s?):\/\//g.test(host)) {
        return [name, 'Enter the hostname only without http/https'];
    }
    return null;
};

const FORM_VALIDATORS = valid.rules([
    validateHost('host'),
    valid.notEmpty('username'),
    valid.notEmpty('password'),
    valid.notEmpty('company_name'),
]);

const validateForm = data => valid.validate(FORM_VALIDATORS, data);

const INIT_VALUES = {
    host: '',
    company_id: '',
    public_key: '',
    private_key: '',
    integration_status: false,
};

export default ({
    created = false,
    service = {},
    context,
    onSave,
    onVerify,
    onCancel,
    onRemove,
}) => {
    const [disabled, setDisabled] = useState(created);

    const [status, setStatus] = useState({
        loading: false,
        success: !created,
        failed: false,
    });
    const { extra = {} } = service;
    const { config } = context;
    const [values, setValues] = useState({ ...INIT_VALUES, ...extra });
    const [errors, setErrors] = useState({});

    const changeFactory = name => value => {
        if (name !== 'integration_status') {
            setStatus({});
        }
        setValues({
            ...values,
            [name]: typeof value === 'string' ? value.trim() : value,
        });
    };

    const fieldFactory = (name, label, secured = false) => {
        const Component = secured ? PasswordField : Fields.Text;
        return (
            <Fields.Row>
                <Component
                    label={label}
                    value={values[name]}
                    error={errors[name]}
                    onChange={changeFactory(name)}
                />
            </Fields.Row>
        );
    };

    const onVerifyValidate = () => {
        validateForm(values).then(errors => {
            setErrors(errors || {});
            if (!errors) {
                setStatus({ loading: true });
                onVerify({ ...values, integration_status: true }).then(
                    status => {
                        if (status) {
                            setStatus({ success: true });
                            setValues({ ...values, integration_status: true });
                            setDisabled(false);
                        } else {
                            setStatus({ failed: true });
                        }
                    }
                );
            }
        });
    };
    const onNext = () =>
        onSave(created ? extendByDefaultSchedule(values) : values);
    return (
        <PaperContainer title={`${config.label} Integration`}>
            <AuthenticationInfo config={config} />
            {fieldFactory('host', 'Host*')}
            {fieldFactory('username', 'Username*')}
            {fieldFactory('password', 'Password*', true)}
            {fieldFactory('company_name', 'Company name*')}
            <StatusActionForm
                status={status}
                disabled={status.loading}
                onVerify={onVerifyValidate}
            />
            <IntegrationStatus
                value={values.integration_status}
                disabled={disabled || !status.success}
                onChange={changeFactory('integration_status')}
            />
            {onRemove && <HelpLink config={config} />}

            <ButtonsForm withLeft>
                {onRemove && (
                    <ButtonsForm.BackButton label="Delete" onClick={onRemove} />
                )}
                {!onRemove && <HelpLink config={config} />}
                <div>
                    {!created && (
                        <ButtonsForm.BackButton onClick={() => onCancel()} />
                    )}
                    <ButtonsForm.NextButton
                        label="Save"
                        disabled={disabled || !status.success}
                        onClick={onNext}
                    />
                </div>
            </ButtonsForm>
        </PaperContainer>
    );
};

const IntegrationStatus = ({ value, disabled, onChange }) => (
    <Fields.Row>
        <Fields.Switch
            value={value}
            onChange={onChange}
            legend="Integration status"
            label={value ? 'Enabled' : 'Disabled'}
            disabled={disabled}
        />
    </Fields.Row>
);
